import { SwapPrice } from '@/core/swap';
import { SwapType, calculatePriceImpact } from '@/domain/swap';
import {
  type Address,
  Zero,
  getTotalTokenIds,
  type TokenIds,
  Permit2Quote,
  MarketplacePrice,
  MarketplaceQuote,
} from 'nftx.js';
import { fetchPrice, fetchQuote } from 'nftx.js';
import { parseEther } from 'viem';

type FetchQuote = typeof fetchQuote;
type FetchPrice = typeof fetchPrice;
type Permit2 = Permit2Quote;

export const makeFetchBuyAmount = ({
  fetchPrice,
  fetchQuote,
}: {
  fetchPrice: FetchPrice;
  fetchQuote: FetchQuote;
}) =>
  async function fetchBuyAmount({
    network,
    userAddress,
    slippagePercentage,
    permit2,

    sellType,
    sellToken,
    sellAmount,
    sellTokenIds,
    sellVaultId,

    buyType,
    buyToken,
    buyTokenIds,
  }: {
    network: number;
    userAddress?: Address;
    slippagePercentage: number;
    permit2?: Permit2;

    sellType: SwapType;
    sellToken: Address;
    sellAmount: `${number}`;
    sellTokenIds: TokenIds;
    sellVaultId: string;

    buyType: SwapType;
    buyToken: Address;
    buyTokenIds: TokenIds;
  }): Promise<SwapPrice | null> {
    const defaultPrice = {
      approveContracts: [],
      feePrice: Zero,
      premiumPrice: Zero,
      price: Zero,
      vTokenPrice: Zero,
    };
    const defaultParams = { approveContracts: [], methodParameters: {} };
    const noPrice = null;

    // Swap ETH for ETH
    if (
      sellType === 'ETH' &&
      buyType === 'ETH' &&
      sellAmount &&
      Number(sellAmount)
    ) {
      return noPrice;
    }

    // Swap ETH for vToken
    if (sellType === 'ETH' && buyType === 'vTOKEN') {
      if (
        !sellAmount ||
        !Number(sellAmount) ||
        Number(sellAmount) < 0 ||
        !buyToken
      ) {
        return noPrice;
      }
      // Get the amount of vToken (buyToken) you'd get for selling (sellAmount) ETH
      let quoteInVToken: MarketplaceQuote | MarketplacePrice;
      if (userAddress) {
        quoteInVToken = await fetchQuote({
          type: 'erc20',
          sellToken: 'ETH',
          sellAmount: parseEther(sellAmount),
          network,
          permit2,
          slippagePercentage,
          buyToken,
          userAddress,
        });
      } else {
        quoteInVToken = await fetchPrice({
          type: 'erc20',
          sellToken: 'ETH',
          sellAmount: parseEther(sellAmount),
          network,
          buyToken,
        });
      }

      const { price } = quoteInVToken;

      let priceImpact = 0;
      try {
        const buyPrice = await fetchPrice({
          network,
          type: 'erc20',
          buyToken: 'ETH',
          buyAmount: parseEther(sellAmount),
          sellToken: buyToken,
        });
        priceImpact = calculatePriceImpact(price, buyPrice.price);
      } catch {
        // Unable to calculate price impact
      }

      return {
        ...defaultPrice,
        ...quoteInVToken,
        vTokenPrice: price,
        amount: price,
        priceImpact,
        methodParameters: {
          ...('methodParameters' in quoteInVToken
            ? quoteInVToken.methodParameters
            : {}),
          vaultId: sellVaultId,
        },
      };
    }

    // Swap vToken for ETH
    if (sellType === 'vTOKEN' && buyType === 'ETH') {
      if (
        !sellToken ||
        !sellAmount ||
        !Number(sellAmount) ||
        Number(sellAmount) < 0
      ) {
        return noPrice;
      }
      // Get the amount of ETH you'd get for selling (sellAmount) vToken (sellToken)
      let quoteInEth: MarketplaceQuote | MarketplacePrice;

      if (userAddress) {
        quoteInEth = await fetchQuote({
          network,
          type: 'erc20',
          sellToken,
          userAddress,
          sellAmount: parseEther(sellAmount),
          slippagePercentage,
          buyToken: 'ETH',
          permit2,
        });
      } else {
        quoteInEth = await fetchPrice({
          network,
          type: 'erc20',
          sellToken,
          sellAmount: parseEther(sellAmount),
          buyToken: 'ETH',
        });
      }

      const { price } = quoteInEth;

      let priceImpact = 0;
      try {
        const buyPrice = await fetchPrice({
          type: 'erc20',
          network,
          buyToken: sellToken,
          buyAmount: parseEther(sellAmount),
          sellToken: 'ETH',
        });
        priceImpact = calculatePriceImpact(price, buyPrice.price);
      } catch {
        // Unable to calculate price impact
      }

      return {
        ...defaultPrice,
        ...quoteInEth,
        vTokenPrice: price,
        amount: price,
        priceImpact,
        methodParameters: {
          ...('methodParameters' in quoteInEth
            ? quoteInEth.methodParameters
            : {}),
          vaultId: sellVaultId,
        },
      };
    }

    // Swap vToken for vToken
    if (sellType === 'vTOKEN' && buyType === 'vTOKEN') {
      if (
        !sellToken ||
        !sellAmount ||
        !Number(sellAmount) ||
        Number(sellAmount) < 0 ||
        !buyToken
      ) {
        return noPrice;
      }
      // Get the amount of vToken (buyToken) you'd get for selling (sellAmount) of vToken (sellToken)
      let quoteInVToken: MarketplaceQuote | MarketplacePrice;
      if (userAddress) {
        quoteInVToken = await fetchQuote({
          network,
          type: 'erc20',
          sellToken,
          userAddress,
          sellAmount: parseEther(sellAmount),
          slippagePercentage,
          permit2,
          buyToken,
        });
      } else {
        quoteInVToken = await fetchPrice({
          network,
          type: 'erc20',
          sellToken,
          sellAmount: parseEther(sellAmount),
          buyToken,
        });
      }

      const { price } = quoteInVToken;

      let priceImpact = 0;
      try {
        const buyPrice = await fetchPrice({
          type: 'erc20',
          network,
          buyToken: sellToken,
          buyAmount: parseEther(sellAmount),
          sellToken: buyToken,
        });
        priceImpact = calculatePriceImpact(price, buyPrice.price);
      } catch {
        // Unable to calculate price impact
      }

      return {
        ...defaultPrice,
        ...quoteInVToken,
        vTokenPrice: price,
        amount: price,
        priceImpact,
        methodParameters: {
          ...('methodParameters' in quoteInVToken
            ? quoteInVToken.methodParameters
            : {}),
          vaultId: sellVaultId,
        },
      };
    }

    // Swap NFTs for ETH
    if (sellType === 'NFT' && buyType === 'ETH') {
      if (!sellVaultId || !sellTokenIds.length) {
        return noPrice;
      }

      let buyPrice = 0n;
      try {
        const buyQuote = await fetchPrice({
          type: 'buy',
          network,
          vaultId: sellVaultId,
          tokenIds: sellTokenIds,
        });
        buyPrice = buyQuote.price;
      } catch {
        // Unable to determine buy price
      }

      if (userAddress) {
        const quoteInEth = await fetchQuote({
          type: 'sell',
          tokenIds: sellTokenIds,
          vaultId: sellVaultId,
          network,
          userAddress,
          slippagePercentage,
        });

        return {
          ...quoteInEth,
          priceImpact: calculatePriceImpact(quoteInEth.price, buyPrice),
          amount: quoteInEth.price,
        };
      }

      const quoteInEth = await fetchPrice({
        type: 'sell',
        network,
        vaultId: sellVaultId,
        tokenIds: sellTokenIds,
      });

      return {
        ...defaultParams,
        ...quoteInEth,
        priceImpact: calculatePriceImpact(quoteInEth.price, buyPrice),
        amount: quoteInEth.price,
      };
    }

    // Swap NFTs for vToken
    if (sellType === 'NFT' && buyType === 'vTOKEN') {
      if (!sellTokenIds.length) {
        return noPrice;
      }

      const priceInVToken = parseEther(`${getTotalTokenIds(sellTokenIds)}`);
      const priceImpact = 0;

      if (userAddress) {
        const quoteInEth = await fetchQuote({
          type: 'mint',
          tokenIds: sellTokenIds,
          vaultId: sellVaultId,
          network,
          userAddress,
          slippagePercentage,
        });

        return {
          ...quoteInEth,
          priceImpact,
          amount: priceInVToken,
        };
      }

      const quoteInEth = await fetchPrice({
        type: 'mint',
        tokenIds: sellTokenIds,
        vaultId: sellVaultId,
        network,
      });

      return {
        ...defaultParams,
        ...quoteInEth,
        priceImpact,
        amount: priceInVToken,
      };
    }

    // Swap NFTs for NFTs
    if (sellType === 'NFT' && buyType === 'NFT') {
      if (
        !sellVaultId ||
        !buyTokenIds.length ||
        getTotalTokenIds(buyTokenIds) !== getTotalTokenIds(sellTokenIds)
      ) {
        return noPrice;
      }

      const priceImpact = 0;

      if (userAddress) {
        const quoteInEth = await fetchQuote({
          type: 'swap',
          network,
          vaultId: sellVaultId,
          mintTokenIds: sellTokenIds,
          redeemTokenIds: buyTokenIds,
          userAddress,
          slippagePercentage,
        });

        return {
          ...quoteInEth,
          priceImpact,
          amount: Zero,
        };
      }

      const quoteInEth = await fetchPrice({
        type: 'swap',
        network,
        vaultId: sellVaultId,
        redeemTokenIds: buyTokenIds,
        mintTokenIds: sellTokenIds,
      });

      return {
        ...defaultParams,
        ...quoteInEth,
        priceImpact,
        amount: Zero,
      };
    }

    // Anything else is invalid or not calculatable
    return noPrice;
  };

export default makeFetchBuyAmount({
  fetchPrice,
  fetchQuote,
});
